import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PublicLayout from './commodity/components/layout/PublicLayout';
import AuthChoicePage from './core/identity-access/screens/AuthChoicePage';
import Register from './core/identity-access/screens/Register';
import Login from './core/identity-access/screens/Login';
import VerifyEmail from './core/identity-access/screens/VerifyEmail';
import ForgotPassword from './core/identity-access/screens/ForgotPassword';
import {
  getStoredTokens,
  saveTokens,
  clearTokens,
  isTokenExpired,
  decodeIdToken,
} from './adapters/api-client/tokenStorage';
import { login, setInitialized } from './core/identity-access/store/authSlice';
import { AppDispatch, RootState } from './store/store';
import ProtectedRoute from './core/identity-access/components/ProtectedRoute';
import { CariaUiGallery } from './verticals/caria';
import VehicleFeedV3 from './verticals/caria/screens/VehicleFeedV3';
import Home from './verticals/caria/screens/Home';
import CatalogFeed from './verticals/caria/screens/CatalogFeed';
import CatalogDetail from './verticals/caria/screens/CatalogDetail';
import { PageScaffold } from './commodity/components/caria';
import { useAuthChrome } from './core/identity-access/hooks/useAuthChrome';
import MyContacts from './verticals/caria/screens/MyContacts';
import Profile from './verticals/caria/screens/Profile';
import AdminModeration from './verticals/caria/screens/AdminModeration';
import QaGovernance from './core/qa-governance/screens/QaGovernance';
import SavedListings from './verticals/caria/screens/SavedListings';
import SellerDashboard from './verticals/caria/screens/SellerDashboard';
import DealerDashboard from './verticals/caria/screens/DealerDashboard';
import VehiclePublish from './verticals/caria/screens/VehiclePublish';
import { EXAMPLE_PAGES, ExamplePage } from './verticals/caria/screens/ExamplePages';
import UiGallery from './commodity/components/caria/UiGallery';

// CAR-IA vertical screens land in Epic 4+. Epic 4 adds the buyer Feed/Detail
// skeletons and the dev-only UI gallery (S4.4); Epic 7 binds the data.

const NotFound: React.FC = () => {
  // PageScaffold defaults to signed-out and requires callers to supply the real
  // identity (that default is the DEF-004 fix). Without this a signed-in user
  // who mistypes a URL sees "Ingresar" and may think their session dropped.
  const authChrome = useAuthChrome();
  return (
    <PageScaffold
      {...authChrome}
      title="Página no encontrada"
      eyebrow="Error 404"
      lead="La dirección que abriste no existe o el anuncio ya no está disponible."
      width="narrow"
    >
      <p style={{ color: 'var(--ink-4)', fontSize: 14 }}>
        Revisa el enlace, o vuelve al catálogo para seguir explorando.
      </p>
    </PageScaffold>
  );
};

// ── App ───────────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  // Restore session on page load.
  // Tokens are persisted in localStorage under 'car-ia:*' keys after login.
  useEffect(() => {
    if (isAuthenticated) return;

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    async function restoreSession() {
      const stored = getStoredTokens();

      if (!stored.idToken) {
        dispatch(setInitialized());
        return;
      }

      let currentIdToken: string = stored.idToken;

      if (isTokenExpired(currentIdToken)) {
        if (!stored.refreshToken) {
          clearTokens();
          dispatch(setInitialized());
          return;
        }
        try {
          const res = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: stored.refreshToken }),
          });
          if (!res.ok) throw new Error('refresh failed');
          const data = await res.json();
          saveTokens({
            idToken: data.idToken,
            refreshToken: stored.refreshToken,
            expiresIn: data.expiresIn,
          });
          currentIdToken = data.idToken;
        } catch {
          clearTokens();
          dispatch(setInitialized());
          return;
        }
      }

      const claims = decodeIdToken(currentIdToken);
      if (!claims) {
        clearTokens();
        dispatch(setInitialized());
        return;
      }

      dispatch(
        login({
          user: {
            id: claims.sub,
            email: claims.email,
            role: claims['custom:role'] ?? '',
            firstName: claims.given_name ?? '',
            lastName: claims.family_name ?? '',
          },
          accessToken: currentIdToken,
        }),
      );
    }

    restoreSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Routes>
      {/* Auth routes. /login, /register and /forgot-password render standalone
          (full-viewport split pane / recovery card ported from the prototypes),
          so they must NOT nest inside PublicLayout's chrome. */}
      <Route
        path="/auth"
        element={
          <PublicLayout>
            <AuthChoicePage />
          </PublicLayout>
        }
      />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/verify-email"
        element={
          <PublicLayout>
            <VerifyEmail />
          </PublicLayout>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* CAR-IA dev-only UI gallery (Epic 4, S4.4). Self-disables in
          production; route kept out of the protected tree so UX/QA can
          review component states without an account. */}
      <Route
        path="/caria/ui-gallery"
        element={
          <PublicLayout>
            <CariaUiGallery />
          </PublicLayout>
        }
      />

      {/* Public marketplace — buyer discovery (no auth required for browsing).
          These render standalone (own TopBar + BottomNav mobile shell, no
          PublicLayout chrome). /feed is the v3 design showcase on seed data. */}
      <Route path="/search" element={<CatalogFeed />} />
      <Route path="/vehicles/:id" element={<CatalogDetail />} />
      <Route path="/feed" element={<VehicleFeedV3 />} />

      {/* v3 design-system catalog (dev/design reference) */}
      <Route path="/ui-gallery" element={<UiGallery />} />

      {/* Example / future-scope pages (financing, payments, reviews, help, …).
          Prototype-styled scaffolds; routes generated from EXAMPLE_PAGES. */}
      {EXAMPLE_PAGES.map((spec) => (
        <Route key={spec.path} path={spec.path} element={<ExamplePage spec={spec} />} />
      ))}

      {/* Buyer + admin account surfaces — standalone, each carries its own
          TopBar + BottomNav, so none may nest inside MainLayout's forest-green
          sidebar chrome. /vehicles was a stale placeholder that duplicated the
          shipped feed at /search, so it redirects there. */}
      <Route path="/vehicles" element={<Navigate to="/search" replace />} />
      <Route
        path="/my-contacts"
        element={
          <ProtectedRoute>
            <MyContacts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/moderation"
        element={
          <ProtectedRoute>
            <AdminModeration />
          </ProtectedRoute>
        }
      />

      {/* QA Governance console (Week12 / PC-07) — ADMIN only; standalone shell
          like /admin/moderation, so it must not nest inside MainLayout. */}
      <Route
        path="/admin/qa-governance"
        element={
          <ProtectedRoute>
            <QaGovernance />
          </ProtectedRoute>
        }
      />

      {/* Saved listings — standalone: carries its own TopBar + BottomNav, so it
          must NOT nest inside MainLayout (forest-green sidebar chrome). */}
      <Route
        path="/saved"
        element={
          <ProtectedRoute>
            <SavedListings />
          </ProtectedRoute>
        }
      />

      {/* Buyer Home (W00) — mobile shell, no PublicLayout chrome */}
      <Route path="/" element={<Home />} />

      {/* Seller mobile-shell screens — auth-guarded but standalone: they carry
          their own TopBar + BottomNav, so they must NOT nest inside MainLayout
          (that would double the chrome). Same pattern as Home/feed above. */}
      <Route
        path="/my-listings"
        element={
          <ProtectedRoute>
            <SellerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dealer"
        element={
          <ProtectedRoute>
            <DealerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publish"
        element={
          <ProtectedRoute>
            <VehiclePublish />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles/:id/publish"
        element={
          <ProtectedRoute>
            <VehiclePublish />
          </ProtectedRoute>
        }
      />

      {/* 404 — on the CAR-IA chrome like every other surface. With the account
          routes migrated, nothing renders inside MainLayout any more; the
          forest-green sidebar shell is now unused by the router. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;
